
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Puzzle } from 'lucide-react'
import { useCreateExtension } from '@/hooks'

interface RegisterFormData {
  name: string
  description: string
  version: string
  developerId: string
  repositoryUrl: string
  homepageUrl: string
  category: string
  isPublic: boolean
  priceCents: number
}

interface RegisterExtensionModalProps {
  isOpen: boolean
  onClose: () => void
  createExtension: ReturnType<typeof useCreateExtension>['mutate']
  isPending: boolean
}

export function RegisterExtensionModal({ isOpen, onClose, createExtension, isPending }: RegisterExtensionModalProps) {
  const [formData, setFormData] = useState<RegisterFormData>({
    name: '',
    description: '',
    version: '1.0.0',
    developerId: '',
    repositoryUrl: '',
    homepageUrl: '',
    category: '',
    isPublic: true,
    priceCents: 0,
  })

  const handleSubmit = async () => {
    createExtension({
      name: formData.name,
      version: formData.version,
      description: formData.description,
      developerId: formData.developerId,
      category: formData.category,
    } as any)

    onClose()
    setFormData({
      name: '',
      description: '',
      version: '1.0.0',
      developerId: '',
      repositoryUrl: '',
      homepageUrl: '',
      category: '',
      isPublic: true,
      priceCents: 0,
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Register New Extension</CardTitle>
          <CardDescription>
            Submit a new extension to the marketplace
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Extension Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="My Extension"
              />
            </div>
            <div className="space-y-2">
              <Label>Version *</Label>
              <Input
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="1.0.0"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of your extension"
              />
            </div>
            <div className="space-y-2">
              <Label>Developer ID *</Label>
              <Input
                value={formData.developerId}
                onChange={(e) => setFormData({ ...formData, developerId: e.target.value })}
                placeholder="dev_123"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="analytics, productivity, integration..."
              />
            </div>
            <div className="space-y-2">
              <Label>Repository URL</Label>
              <Input
                value={formData.repositoryUrl}
                onChange={(e) => setFormData({ ...formData, repositoryUrl: e.target.value })}
                placeholder="https://github.com/user/repo"
              />
            </div>
            <div className="space-y-2">
              <Label>Homepage URL</Label>
              <Input
                value={formData.homepageUrl}
                onChange={(e) => setFormData({ ...formData, homepageUrl: e.target.value })}
                placeholder="https://example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Price (cents)</Label>
              <Input
                type="number"
                value={formData.priceCents}
                onChange={(e) => setFormData({ ...formData, priceCents: parseInt(e.target.value) || 0 })}
                placeholder="0 for free"
              />
              <p className="text-xs text-muted-foreground">
                {formData.priceCents === 0 ? 'Free' : `${(formData.priceCents / 100).toLocaleString()} credits`}
              </p>
            </div>
          </div>
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSubmit} disabled={isPending} className="gap-2">
              <Puzzle className="h-4 w-4" />
              Register Extension
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
